import type { ResolvedComponent } from "@inertiajs/svelte"

import PersistentLayout from "@/layouts/persistent-layout.svelte"

// Shared by the client entry (entrypoints/inertia.ts) and the SSR entry
// (ssr/ssr.ts): resolves an Inertia page name to its component + layout
// stack, so the two bundles can't drift apart.
//
// A page may export its own (persistent) `layout`. It nests UNDER the global
// PersistentLayout (i18n + flash + toaster) so that always stays mounted,
// while the page's layout — e.g. the settings nav rail — persists across
// navigations within its section instead of remounting. Supports a single
// layout or an array of nested layouts.
//
// Pages in the settings area get the settings layout by default instead of
// each repeating the same `<script module>` export; `export const layout =
// null` opts a settings-area page out (the chrome-less print poster).
const SETTINGS_PAGES = /^(?:settings|website\/settings|website\/analytics)\//

export async function resolvePage(name: string): Promise<ResolvedComponent> {
  const pages = import.meta.glob<ResolvedComponent>([
    "../pages/**/*.svelte",
    "../pages/**/*.md",
  ])
  const resolver =
    pages[`../pages/${name}.svelte`] ?? pages[`../pages/${name}.md`]
  if (!resolver) {
    console.error(`Missing Inertia page component: '${name}'`)
    return undefined as unknown as ResolvedComponent
  }
  const page = await resolver()
  const pageLayout =
    "layout" in page ? page.layout : await defaultLayout(name)
  return {
    default: page.default,
    layout: pageLayout
      ? [
          PersistentLayout,
          ...(Array.isArray(pageLayout) ? pageLayout : [pageLayout]),
        ]
      : PersistentLayout,
  } as ResolvedComponent
}

// Lazy so the settings layout (and its nav) stays out of the entry chunk —
// non-settings pages never load it. The dynamic import resolves to the same
// module instance every time, so the layout still persists across
// in-section navigations.
async function defaultLayout(name: string) {
  if (!SETTINGS_PAGES.test(name)) return undefined
  return (await import("@/layouts/settings/layout.svelte")).default
}
